<script>
  import findIndex from 'lodash/findIndex';
  import { DateTime } from 'luxon';
  import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
  import ReportModel from '@models/Report';
  import CarUseModel from '@models/CarUse';
  import FileDownloadLink from '@components/FileDownloadLink';
  // import BlockingContainer from '@components/BlockingContainer';
  import CarUseForm from '@views/reports/car-use-form';
  import CarUseModal from '@views/reports/edit-car-use';

  export default {
    components: {
      // BlockingContainer,
      FontAwesomeIcon,
      CarUseForm,
      CarUseModal,
      FileDownloadLink,
    },
    props: {
      collapsed: {
        default: false,
        type: Boolean,
        required: false,
      },
      // eslint-disable-next-line
      loadedId: {
        default: null,
      },
      // eslint-disable-next-line
      reportId: {
        required: true,
      },
      carUses: {
        default: () => [],
        type: Array,
      },
      locked: {
        type: Number,
        default: 0,
      },
      completed: {
        type: Number,
        default: 0,
      },
    },
    data() {
      return {
        selectedCarUse: null,
        fields: [
          {
            key: 'id',
            label: this.$t('table_labels.id'),
            sortable: true,
          },
          {
            key: 'car_type',
            label: this.$t('fields.car_type'),
            sortable: false,
          },
          {
            key: 'license_plate',
            label: this.$t('table_labels.license_plate'),
            sortable: true,
          },
          {
            key: 'owner',
            label: this.$t('fields.owner'),
            sortable: false,
          },
          {
            key: 'takeover_from',
            label: this.$t('fields.takeover_from'),
            sortable: true,
          },
          {
            key: 'return_to',
            label: this.$t('fields.return_to'),
            sortable: true,
          },
          {
            key: 'takeover_date',
            label: this.$t('fields.takeover_date'),
            sortable: true,
            formatter: (value) => (value ? DateTime.fromSQL(value).toFormat('dd.MM.yyyy HH:mm') : ''),
          },
          {
            key: 'return_date',
            label: this.$t('fields.return_date'),
            sortable: true,
            formatter: (value) => (value ? DateTime.fromSQL(value).toFormat('dd.MM.yyyy HH:mm') : ''),
          },
          {
            key: 'km_status_takeover',
            label: this.$t('fields.km_status_takeover'),
            sortable: true,
          },
          {
            key: 'km_status_return',
            label: this.$t('fields.km_status_return'),
            sortable: true,
          },
          {
            key: 'total_km',
            label: this.$t('fields.total_km'),
            sortable: false,
          },
          {
            key: 'completed',
            label: this.$t('fields.status'),
            sortable: false,
          },
        ],
      };
    },
    mounted() {
      if (!this.locked && !this.completed) {
          this.fields.push({
              key: 'actions',
              label: this.$t('table_labels.actions'),
              sortable: false,
          });
      }
    },
    methods: {
      submit(carUse) {
        this.$emit('submitted', carUse);
      },
      update(carUse) {
        this.selectedCarUse = null;
        const key = findIndex(this.carUses, { id: carUse.id });
        this.$emit('updated', carUse, key);
        if (!carUse.completed) this.$store.dispatch('common/updateCurrentReportStatus', 0);
      },
      async deleteCarUse(id) {
        if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.car_use') }))) {
          this.$wait.start(`carUse${id}`);
          const key = findIndex(this.carUses, { id });
          const report = new ReportModel({ id: this.reportId });
          const carUse = new CarUseModel({ id });
          await carUse.for(report).delete();
          this.$emit('deleted', id, key);
          this.$wait.end(`carUse${id}`);
        }
      },
      dateDiff(item) {
        const date = DateTime.fromSQL(item.return_date)
                .diff(DateTime.fromSQL(item.takeover_date), ['hours', 'minutes'])
                .toObject();
        return `${date.hours}h ${date.minutes}m`;
      },
    },
  };
</script>

<template>
  <div>
    <b-collapse id="car-uses-collapse" v-model="collapsed" class="mt-2">
      <CarUseForm v-if="!locked && !completed" ref="carUseForm" :loaded-id="loadedId" :report-id="reportId" @submitted="submit" />
    </b-collapse>
    <small v-if="carUses.length" class="table-responsive">
      <br/>
      <b-table stacked="lg" striped hover :items="carUses" :fields="fields" small class="no-min-width">
        <template v-slot:cell(car_type)="row">
          <div v-if="row.item.car && row.item.car.car_type">
            {{ row.item.car.car_type.name }}
          </div>
          <div v-else> - </div>
        </template>
        <template v-slot:cell(license_plate)="row">
          <div v-if="row.item.car">
            {{ row.item.car.license_plate }}
          </div>
          <div v-else> - </div>
        </template>
        <template v-slot:cell(total_km)="row">
          <template v-if="row.item.km_status_return && row.item.km_status_takeover">
            {{ row.item.km_status_return - row.item.km_status_takeover }}
          </template>
          <template v-else>
            -
          </template>
        </template>        
        <template v-slot:cell(owner)="row">
          <div v-if="row.item.car && row.item.car.car_owner" class="no-wrap">{{ row.item.car.car_owner.name }}</div>
          <div v-else>-</div>
        </template>
        <template v-slot:cell(completed)="row">
          <FontAwesomeIcon v-if="row.item.completed" class="text-success" :icon="['fas', 'check-circle']" />
          <FontAwesomeIcon v-else class="text-danger" :icon="['fas', 'times-circle']" />
        </template>
        <template v-slot:cell(actions)="row">
          <div class="d-flex">
            <FileDownloadLink
              v-if="row.item.car && row.item.car.zub"
              :file="row.item.car.zub"
              :removable="false"
              :small-text="true"
              :hide-text="true"
              btn-class="mr-2 btn btn-sm btn-outline-secondary">
                <FontAwesomeIcon :icon="['fas', 'sticky-note']" style="margin-top: 0.35rem!important;"/>
            </FileDownloadLink>
            <b-btn class="mr-2" size="sm" variant="outline-primary" @click="selectedCarUse = row.item.id">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
            </b-btn>
            <b-btn class="mr-2" size="sm" variant="outline-danger" @click="deleteCarUse(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
            </b-btn>
          </div>
        </template>
      </b-table>
    </small>

    <CarUseModal v-if="!locked && !completed" v-model="selectedCarUse" :loaded-id="selectedCarUse" :report-id="reportId" @updated="update" @hidden="selectedCarUse = null" />
  </div>
</template>
